import { Grid, Tooltip } from "@material-ui/core";
import { useEffect, useContext, useRef, useCallback, useMemo } from "react";

import Fieldset from "../../../../components/basics/Fieldset";
import Chip from "../../../../components/basics/Chip";
import SelectErp from "../../../../components/basics/SelectErp";
import { HeaderAccordion, Line } from "../styles/ClassificacaoServico";
import { indexTypeContractByCompany } from "../../../../repositories/qerpapi/erp/faturamento/TipoContratosRepository";

import { ClassificacaoServicoContext } from "../contexts/ClassificacaoServicoContext";
import { QueryParamsContext } from "../../../../context/QueryParamsContext";

import {
  getClassificacaoTributaria,
  getIndicadorOperacao,
  getCodigoTributacao,
  getNbs,
  getNbsDados,
} from "../../../../repositories/qerpapi/impostos/ImpostosRepository";

const AccordionClassifReforma = ({ ...props }) => {
  const {
    servicos,
    setServicos,
    nbs,
    setNbs,
    indicadorOperacao,
    setIndicadorOperacao,
    classificacaoTributaria,
    setClassificacaoTributaria,
    codigoTributacao,
    setCodigoTributacao,
    classificacaoOptions,
    setClassificacaoOptions,
    indicadorOptions,
    setIndicadorOptions,
    codTributacaoOptions,
    setCodTributacaoOptions,
    nbsOptions,
    setNbsOptions,
    servicosOptions,
    setServicosOptions,
  } = useContext(ClassificacaoServicoContext);

  const { queryParams } = useContext(QueryParamsContext);

  const allClassificacaoOptionsRef = useRef([]);
  const allIndicadorOptionsRef = useRef([]);
  const nbsDataRef = useRef([]);
  const allNbsOptionsRef = useRef([]);

  const fetchData = useCallback(async () => {
    if (!queryParams?.empresa_cnpj || !queryParams?.empresa_id) return;

    try {
      const [
        servicosData,
        classificacaoData,
        indicadorData,
        codTributacaoData,
        nbsData,
      ] = await Promise.all([
        indexTypeContractByCompany({
          empresa_cnpj: queryParams.empresa_cnpj,
          empresa_id: queryParams.empresa_id,
          where: { ativo: "S" },
        }),
        getClassificacaoTributaria(),
        getIndicadorOperacao(),
        getCodigoTributacao(),
        getNbs(),
      ]);

      setServicosOptions(servicosData.data);
      setClassificacaoOptions(classificacaoData);
      setIndicadorOptions(indicadorData);
      setCodTributacaoOptions(codTributacaoData);

      allNbsOptionsRef.current = nbsData || [];
      setNbsOptions(nbsData);

      allClassificacaoOptionsRef.current = classificacaoData || [];
      allIndicadorOptionsRef.current = indicadorData || [];
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    }
  }, [
    queryParams?.empresa_cnpj,
    queryParams?.empresa_id,
    setServicosOptions,
    setClassificacaoOptions,
    setIndicadorOptions,
    setCodTributacaoOptions,
    setNbsOptions,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filterNbsByService = useCallback(
    (selectedService, currentNbsValue) => {
      const allNbs = allNbsOptionsRef.current;
      const currentLc116 = selectedService?.nfse_codigo;

      if (!allNbs || allNbs.length === 0) return;

      if (!currentLc116) {
        if (allNbs.length > 0) {
          setNbsOptions(allNbs);
        }
        return;
      }

      const filteredNbs = allNbs.filter((nbsItem) => {
        if (!nbsItem?.itemLc11) return false;

        const normalizedItemLc116 = nbsItem.itemLc11.replace(/\./g, "");
        const normalizedServiceLc116 = currentLc116.replace(/\./g, "");

        return normalizedItemLc116 === normalizedServiceLc116;
      });

      setNbsOptions(filteredNbs);

      if (
        currentNbsValue &&
        !filteredNbs.find((n) => n.codigo === currentNbsValue.codigo)
      ) {
        setNbs(null);
        setIndicadorOperacao(null);
        setClassificacaoTributaria(null);
      }
    },
    [setNbsOptions, setNbs, setIndicadorOperacao, setClassificacaoTributaria]
  );

  const handleServiceChange = useCallback(
    (newValue) => {
      setServicos(newValue);
    },
    [setServicos]
  );

  useEffect(() => {
    filterNbsByService(servicos, nbs);
  }, [servicos, filterNbsByService, nbs]);

  const filterClassificacaoByIndicador = useCallback(
    (selectedIndicador) => {
      const nbsData = nbsDataRef.current;
      const allOptions = allClassificacaoOptionsRef.current;

      if (!nbsData || nbsData.length === 0) {
        setClassificacaoOptions(allOptions);
        return;
      }

      const validIds = new Set();

      nbsData.forEach((item) => {
        const hasClassificacao = item?.classificacaoTributaria?.codigo;
        const matchesIndicador =
          !selectedIndicador ||
          item?.indicadorTributacaoConsumo?.codigo === selectedIndicador.codigo;

        if (hasClassificacao && matchesIndicador) {
          validIds.add(item.classificacaoTributaria.codigo);
        }
      });

      if (validIds.size > 0) {
        setClassificacaoOptions(
          allOptions.filter((opt) => validIds.has(opt.codigo))
        );
      } else {
        setClassificacaoOptions(selectedIndicador ? [] : allOptions);
      }
    },
    [setClassificacaoOptions]
  );

  const handleNbsChange = useCallback(
    async (newValue) => {
      setNbs(newValue);

      const resetToDefault = () => {
        nbsDataRef.current = [];
        setClassificacaoOptions(allClassificacaoOptionsRef.current);
        setIndicadorOptions(allIndicadorOptionsRef.current);

        if (!newValue) {
          setClassificacaoTributaria(null);
          setIndicadorOperacao(null);
        }
      };

      if (!newValue?.codigo) {
        resetToDefault();
        return;
      }

      try {
        const nbsData = await getNbsDados(newValue.codigo);
        nbsDataRef.current = nbsData || [];

        const indicadorIds = new Set();

        (nbsData || []).forEach((item) => {
          const codigo = item?.indicadorTributacaoConsumo?.codigo;
          if (codigo) indicadorIds.add(codigo);
        });

        if (indicadorIds.size > 0) {
          setIndicadorOptions(
            allIndicadorOptionsRef.current.filter((option) =>
              indicadorIds.has(option.codigo)
            )
          );
          setIndicadorOperacao(null);
        } else {
          setIndicadorOptions(allIndicadorOptionsRef.current);
        }

        filterClassificacaoByIndicador(null);
      } catch (error) {
        console.error("Erro ao buscar dados do NBS:", error);
        nbsDataRef.current = [];
        setClassificacaoOptions(allClassificacaoOptionsRef.current);
        setIndicadorOptions(allIndicadorOptionsRef.current);
      }
    },
    [
      filterClassificacaoByIndicador,
      setNbs,
      setClassificacaoOptions,
      setIndicadorOptions,
      setClassificacaoTributaria,
      setIndicadorOperacao,
    ]
  );

  const handleIndicadorOperacaoChange = useCallback(
    (newValue) => {
      setIndicadorOperacao(newValue);
      filterClassificacaoByIndicador(newValue);
      setClassificacaoTributaria(null);
    },
    [
      filterClassificacaoByIndicador,
      setIndicadorOperacao,
      setClassificacaoTributaria,
    ]
  );

  const handleClassificaoTributariaChange = useCallback(
    (newValue) => {
      setClassificacaoTributaria(newValue);
    },
    [setClassificacaoTributaria]
  );

  const uniqueNbsOptions = useMemo(() => {
    if (!nbsOptions) return [];

    return Array.from(
      new Map(nbsOptions.map((item) => [item.codigo, item])).values()
    );
  }, [nbsOptions]);

  const textTooltip = useMemo(
    () =>
      "As alterações previstas pela Reforma Tributária para este regime só terão efeito a partir de 2027. Até lá, permanecem válidas as regras atuais, sem necessidade de ajustes ou preocupação com os tributos IBS e CBS.",
    []
  );

  const isReformaTributaria = useMemo(
    () => queryParams?.crt === "3",
    [queryParams]
  );

  return (
    <>
      <Fieldset
        titulo={
          <HeaderAccordion>
            <Grid container spacing={2}>
              <Grid item xs>
                <Chip label="Reforma Tributária" />
              </Grid>
            </Grid>
          </HeaderAccordion>
        }
        bgcolor="white"
        textcolor="black"
        accordion
        defaultExpanded
      >
        <Line container spacing={2}>
          <Grid item xs={2}>
            <SelectErp
              loading={false}
              label="Serviços"
              value={servicos}
              setValue={setServicos}
              onChange={handleServiceChange}
              optionsList={servicosOptions}
              descriptionField="descricao"
            />
          </Grid>

          <Grid item xs={5}>
            <SelectErp
              loading={false}
              label="NBS"
              value={nbs}
              onChange={handleNbsChange}
              setValue={setNbs}
              optionsList={uniqueNbsOptions}
              getOptionLabel={(option) =>
                option ? `${option.codigo} - ${option.descricaoNbs}` : ""
              }
            />
          </Grid>
          <Grid item xs={5}>
            <Tooltip title={!isReformaTributaria ? textTooltip : ""}>
              <span style={{ display: "inline-block", width: "100%" }}>
                <SelectErp
                  loading={false}
                  label="Ind. de Operação de Consumo"
                  value={indicadorOperacao}
                  onChange={handleIndicadorOperacaoChange}
                  disabled={!isReformaTributaria}
                  setValue={setIndicadorOperacao}
                  optionsList={indicadorOptions}
                  getOptionLabel={(option) =>
                    option
                      ? `${option.codigo} - ${option.tipoOperacao} - ${option.tipoOperacao}`
                      : ""
                  }
                />
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={6}>
            <Tooltip title={!isReformaTributaria ? textTooltip : ""}>
              <span style={{ display: "inline-block", width: "100%" }}>
                <SelectErp
                  loading={false}
                  label="Classificação Tributária IBS/CBS"
                  value={classificacaoTributaria}
                  onChange={handleClassificaoTributariaChange}
                  disabled={!isReformaTributaria}
                  setValue={setClassificacaoTributaria}
                  optionsList={classificacaoOptions}
                  getOptionLabel={(option) =>
                    option
                      ? `${option.codigo} - ${option.descricaoReduzida}`
                      : ""
                  }
                />
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={6}>
            <Tooltip title={!isReformaTributaria ? textTooltip : ""}>
              <span style={{ display: "inline-block", width: "100%" }}>
                <SelectErp
                  loading={false}
                  label="Código de Tributação Nacional"
                  value={codigoTributacao}
                  disabled={!isReformaTributaria}
                  setValue={setCodigoTributacao}
                  optionsList={codTributacaoOptions}
                  getOptionLabel={(option) =>
                    option ? `${option.codigo} - ${option.descricao}` : ""
                  }
                />
              </span>
            </Tooltip>
          </Grid>
        </Line>
      </Fieldset>

      <Line />
    </>
  );
};

export default AccordionClassifReforma;
